// @ts-nocheck
import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useQcClassification } from "@/components/quality/qc-form/useQcClassification";
import { trpc } from "@/lib/trpc";
import { ListPageShell } from "@/components/shared/ListPageShell";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, Copy, Trash2, ArchiveRestore, FileCheck2, Layers, PackageCheck, ShieldCheck, Download } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useConfirm } from "@/providers/ConfirmProvider";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import {
  InlineFilterRow,
  type InlineFilterDefinition,
} from "@/components/shared/InlineFilterRow";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, exportToExcel, type ExcelColumnDef } from "@/lib/excel.utils";
import { getStatusOptions, getStatusLabel } from "@/config/status-registry";
import { emsTabClass } from "@/components/ui/ems-tabs";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "code", label: "Mã Biểu Mẫu", width: 22 },
  { key: "name", label: "Tên Biểu Mẫu", width: 36 },
  { key: "type", label: "Phân Hệ QC", width: 16 },
  { key: "scopeLabel", label: "Phạm Vi Áp Dụng", width: 24 },
  { key: "criteriaCount", label: "Số Chỉ Tiêu", width: 14 },
  { key: "version", label: "Phiên Bản", width: 12 },
  { key: "status", label: "Trạng Thái", width: 16 },
  { key: "updatedAt", label: "Ngày Cập Nhật", width: 18 },
];

const FILTER_FIELDS: InlineFilterDefinition[] = [
  { key: "idx", type: "none" },
  { key: "code", placeholder: "Mã mẫu...", type: "text" },
  { key: "name", placeholder: "Tên biểu mẫu...", type: "text" },
  { key: "type", placeholder: "Phân hệ...", type: "text" },
  { key: "scope", placeholder: "Công đoạn / Nhóm VT...", type: "text" },
  { key: "criteriaCount", type: "none" },
  { key: "version", type: "none" },
  {
    key: "status",
    placeholder: "Tất cả",
    type: "select",
    options: getStatusOptions("QCTemplateStatus", false),
  },
  { key: "updatedAt", type: "none" },
  { key: "actions", type: "none" },
];

export default function QCTemplateList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(25);
  const [search, setSearch] = useState("");
  const [scopeTab, setScopeTab] = useState<"ALL" | "IQC" | "IPQC" | "FQC">("ALL");
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const duplicateMutation = useMutation(trpc.qcTemplates.duplicate.mutationOptions());
  const deleteMutation = useMutation(trpc.qcTemplates.delete.mutationOptions());
  const resetDefaultsMutation = useMutation(trpc.qcTemplates.resetDefaults.mutationOptions());

  const handleDuplicate = async (item: any) => {
    try {
      const cloned = await duplicateMutation.mutateAsync({ id: item.id });
      toast.success(`Đã nhân bản thành ${cloned.code}`);
      queryClient.invalidateQueries({ queryKey: [["qcTemplates"]] });
      navigate({ to: `/qc-templates/${cloned.id}/edit` as any });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi nhân bản biểu mẫu QC");
    }
  };

  const handleDelete = async (item: any) => {
    const ok = await confirm({
      title: `Xóa biểu mẫu QC "${item.code}"?`,
      message:
        "Nếu biểu mẫu đã được sử dụng trong các phiếu IQC/IPQC/FQC trước đó, hệ thống sẽ chuyển sang trạng thái Lưu trữ (ARCHIVED) để bảo toàn lịch sử.",
      confirmText: "Xác nhận xóa",
      cancelText: "Hủy",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      const res: any = await deleteMutation.mutateAsync({ id: item.id });
      toast.success(res?._message || `Đã xóa biểu mẫu ${item.code}`);
      queryClient.invalidateQueries({ queryKey: trpc.qcTemplates.list.queryKey() });
    } catch (err: any) {
      toast.error(err?.message || "Không thể xóa biểu mẫu QC");
    }
  };

  const handleResetDefaults = async () => {
    const ok = await confirm({
      title: "Khôi phục 16 Bộ tiêu chuẩn QC gốc ISO?",
      message:
        "Hệ thống sẽ khôi phục lại 16 bộ tiêu chuẩn gốc ngành Bao bì Giấy Offset (6 nhóm IQC, 6 công đoạn IPQC, 4 dòng FQC). Các biểu mẫu bạn tự tạo thêm sẽ được giữ nguyên.",
      confirmText: "Khôi phục tiêu chuẩn gốc",
      cancelText: "Hủy",
    });
    if (!ok) return;
    try {
      const res: any = await resetDefaultsMutation.mutateAsync();
      toast.success(res?._message || "Đã khôi phục bộ tiêu chuẩn gốc ISO");
      queryClient.invalidateQueries({ queryKey: trpc.qcTemplates.list.queryKey() });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khôi phục tiêu chuẩn gốc");
    }
  };

  const { data, isLoading } = useQuery(
    trpc.qcTemplates.list.queryOptions({
      page,
      limit: pageSize,
      search: search || undefined,
      type: scopeTab === "ALL" ? undefined : scopeTab,
      // Mặc định ẩn bản Lưu trữ (phiên bản cũ); chọn trạng thái "Lưu trữ" ở bộ lọc để xem
      status: filterValues.status ? filterValues.status : ["ACTIVE", "DRAFT"],
      // Mỗi mã 1 dòng (bản đang áp dụng / mới nhất) + số phiên bản; lịch sử xem ở trang chi tiết
      latestOnly: true,
    }),
  );

  const items = data?.items || [];
  const total = data?.total || 0;

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearAll = () => {
    setFilterValues({});
    setSearch("");
    setScopeTab("ALL");
  };

  const qcClass = useQcClassification();
  const enrichedItems = useMemo(() => {
    return items.map((item: any) => {
      const criteriaList = Array.isArray(item.criteria)
        ? item.criteria
        : Array.isArray(item.schema?.fields)
          ? item.schema.fields
          : [];
      // Đối tượng áp dụng lấy tên từ danh mục Cấu hình > Chất lượng (không còn viết cứng)
      const parts: string[] = [];
      if (item.materialCategory) parts.push(qcClass.materialLabel(item.materialCategory));
      if (item.processStage) parts.push(qcClass.stageLabel(item.processStage));
      if (item.productCategory && item.type === "FQC") parts.push(qcClass.productGroupLabel(item.productCategory));
      if (item.productType) parts.push(item.productType.name || item.productType.category || item.productType.code);
      const scopeLabel = parts.filter(Boolean).join(" · ") || "Dùng chung";
      return {
        ...item,
        criteriaCount: criteriaList.length,
        scopeLabel,
      };
    });
  }, [items, qcClass]);

  const filteredItems = useMemo(() => {
    return enrichedItems.filter((item: any) => {
      if (filterValues.code && !item.code?.toLowerCase().includes(filterValues.code.toLowerCase())) {
        return false;
      }
      if (filterValues.name && !item.name?.toLowerCase().includes(filterValues.name.toLowerCase())) {
        return false;
      }
      if (filterValues.type && !item.type?.toLowerCase().includes(filterValues.type.toLowerCase())) {
        return false;
      }
      if (filterValues.scope && !item.scopeLabel?.toLowerCase().includes(filterValues.scope.toLowerCase())) {
        return false;
      }
      if (filterValues.status && item.status !== filterValues.status) {
        return false;
      }
      return true;
    });
  }, [enrichedItems, filterValues]);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_bieu_mau_qc_iso",
    columns: EXPORT_COLUMNS,
    localData: filteredItems,
  });

  // ── Chọn nhiều dòng + thao tác hàng loạt ──
  const { hasPermission } = useRoleAccess();
  const rowIds = useMemo(() => filteredItems.map((i: any) => i.id), [filteredItems]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;

  const bulkActions: BulkAction[] = selectedCount
    ? [
        {
          icon: <Download className="h-4 w-4" />,
          label: "Xuất Excel",
          variant: "outline",
          onClick: () =>
            exportToExcel(
              filteredItems.filter((i: any) => selectedIds.has(i.id)),
              EXPORT_COLUMNS,
              "bieu_mau_qc_da_chon",
            ),
        },
        ...(hasPermission("qc-final", "delete")
          ? [
              {
                icon: <Trash2 className="h-4 w-4" />,
                label: `Xoá (${selectedCount})`,
                variant: "destructive" as const,
                onClick: () =>
                  executeBulkAction({
                    confirmFn: confirm,
                    selectedIds: Array.from(selectedIds),
                    actionFn: async (id) => deleteMutation.mutateAsync({ id }),
                    actionName: "xóa",
                    itemType: "biểu mẫu QC",
                    onSuccess: () => {
                      clearSelection();
                      queryClient.invalidateQueries({ queryKey: [["qcTemplates"]] });
                    },
                    requireConfirm: true,
                    confirmMessage: `Xóa ${selectedCount} biểu mẫu QC đã chọn? Biểu mẫu đã dùng trong phiếu sẽ chuyển sang Lưu trữ.`,
                  }),
              },
            ]
          : []),
      ]
    : [];

  return (
    <ListPageShell
      unitLabel="biểu mẫu QC ISO"
      filterFields={FILTER_FIELDS}
      filterValues={filterValues}
      showFilter={showFilter}
      onToggleFilter={() => setShowFilter((prev) => !prev)}
      onClearAll={handleClearAll}
      totalItems={total}
      currentPage={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      actions={
        <ExportImportButtons
          onExport={() => handleExport()}
          isExporting={isExporting}
          onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "mau_bieu_mau_qc")}
        >
          <Button
            size="sm"
            variant="outline"
            disabled={resetDefaultsMutation.isPending}
            onClick={handleResetDefaults}
            className="h-8 text-xs border-slate-300 text-slate-700 hover:bg-slate-100"
            title="Khôi phục 16 bộ tiêu chuẩn ISO gốc ngành Bao bì Giấy Offset"
          >
            <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" /> Khôi Phục ISO
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 font-medium"
            onClick={() => navigate({ to: "/qc-templates/create" as any })}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Tạo Tiêu Chuẩn
          </Button>
        </ExportImportButtons>
      }
    >
      <div className="space-y-3">
        {/* Single-Row Scope Filter Bar (Rule 4) */}
        <div className="flex flex-wrap items-center justify-between gap-2 bg-white px-3 py-2 rounded-md border border-slate-200 shadow-2xs">
          <div role="tablist" aria-label="Phạm vi tiêu chuẩn QC" className="flex items-end gap-1 overflow-x-auto max-w-full">
            <button
              type="button"
              role="tab"
              aria-selected={scopeTab === "ALL"}
              onClick={() => { setScopeTab("ALL"); setPage(1); }}
              className={emsTabClass(scopeTab === "ALL", { size: "sm" })}
            >
              <FileCheck2 className="w-3.5 h-3.5" />
              Tất cả ({total})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scopeTab === "IQC"}
              onClick={() => { setScopeTab("IQC"); setPage(1); }}
              className={emsTabClass(scopeTab === "IQC", { size: "sm" })}
            >
              <PackageCheck className="w-3.5 h-3.5" />
              IQC Vật tư
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scopeTab === "IPQC"}
              onClick={() => { setScopeTab("IPQC"); setPage(1); }}
              className={emsTabClass(scopeTab === "IPQC", { size: "sm" })}
            >
              <Layers className="w-3.5 h-3.5" />
              IPQC Công đoạn
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scopeTab === "FQC"}
              onClick={() => { setScopeTab("FQC"); setPage(1); }}
              className={emsTabClass(scopeTab === "FQC", { size: "sm" })}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              FQC Thành phẩm
            </button>
          </div>
          <span className="text-[11px] text-slate-500 hidden md:inline">
            Chuẩn ISO 9001:2015 & ISO 12647-2 ngành Bao bì Giấy Offset
          </span>
        </div>

        <div className="bg-white rounded-md border border-slate-200 overflow-x-auto shadow-2xs">
          <table className={`w-full text-xs caption-bottom apv-compact-table ${rowSelection.selectionMode ? "apv-select-mode" : ""}`}>
            <thead className="apv-table-header">
              <tr>
                <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                <th className="w-10 text-center text-xs font-bold text-slate-700">#</th>
                <th className="apv-col-code text-left text-xs font-bold text-slate-700">Mã Template</th>
                <th className="text-left text-xs font-bold text-slate-700">Tên Bộ Tiêu Chuẩn QC</th>
                <th className="text-center text-xs font-bold text-slate-700 w-28">Phân Hệ</th>
                <th className="text-left text-xs font-bold text-slate-700">Phạm Vi Áp Dụng (Vật tư / Công đoạn / SP)</th>
                <th className="text-center text-xs font-bold text-slate-700 w-24">Số Chỉ Tiêu</th>
                <th className="text-center text-xs font-bold text-slate-700 w-20">Phiên Bản</th>
                <th className="text-center text-xs font-bold text-slate-700 w-28">Trạng Thái</th>
                <th className="text-left text-xs font-bold text-slate-700 w-32">Cập Nhật</th>
                <th className="apv-col-actions text-right text-xs font-bold text-slate-700 w-20">Thao Tác</th>
              </tr>
              {showFilter && (
                <InlineFilterRow
                  columns={[
                    null,
                    null,
                    { key: "code" },
                    { key: "name" },
                    { key: "type" },
                    { key: "scope" },
                    null,
                    null,
                    { key: "status" },
                    null,
                    null,
                  ]}
                  fields={FILTER_FIELDS}
                  values={filterValues}
                  onChange={handleFilterChange}
                />
              )}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={11} className="p-8 text-center text-slate-400">
                    Đang tải danh sách bộ tiêu chuẩn QC...
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={11} className="p-8 text-center text-slate-400 italic">
                    Không tìm thấy biểu mẫu QC nào phù hợp
                  </td>
                </tr>
              ) : (
                filteredItems.map((item: any, idx: number) => (
                  <DataTableRow
                    key={item.id}
                    id={item.id}
                    rowSelection={rowSelection}
                    className="hover:bg-blue-50/40 cursor-pointer transition-colors group"
                    onClick={() => navigate({ to: `/qc-templates/${item.id}` as any })}
                  >
                    <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} />
                    <td className="text-center text-slate-400">
                      {(page - 1) * pageSize + idx + 1}
                    </td>
                    <td className="apv-col-code font-mono font-bold text-indigo-700 group-hover:underline">
                      {item.code}
                    </td>
                    <td className="font-semibold text-slate-900">
                      <div>{item.name}</div>
                      {item.description && (
                        <div className="text-[11px] font-normal text-slate-500 line-clamp-1">
                          {item.description}
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      <Badge
                        variant="outline"
                        className={
                          item.type === "IQC"
                            ? "bg-blue-50 text-blue-700 border-blue-200 text-[11px] font-bold"
                            : item.type === "IPQC"
                              ? "bg-indigo-50 text-indigo-700 border-indigo-200 text-[11px] font-bold"
                              : item.type === "FQC"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[11px] font-bold"
                                : "bg-slate-50 text-slate-700 border-slate-200 text-[11px]"
                        }
                      >
                        {item.type}
                      </Badge>
                    </td>
                    <td className="text-slate-700 font-medium">
                      {item.scopeLabel}
                    </td>
                    <td className="text-center">
                      <Badge variant="outline" className="font-mono bg-slate-50 text-slate-800 border-slate-200">
                        {item.criteriaCount} chỉ tiêu
                      </Badge>
                    </td>
                    <td className="text-center font-mono font-medium text-slate-600">
                      v{item.version || 1}
                      {item.versionCount > 1 && (
                        <span className="block text-[10px] font-sans font-normal text-slate-500">{item.versionCount} phiên bản</span>
                      )}
                    </td>
                    <td className="text-center">
                      <Badge
                        variant="outline"
                        className={
                          item.status === "ACTIVE" || item.isActive
                            ? "bg-emerald-50 text-emerald-700 border-emerald-300 text-[11px]"
                            : "bg-slate-100 text-slate-600 text-[11px]"
                        }
                      >
                        {getStatusLabel("QCTemplateStatus", item.status || (item.isActive ? "ACTIVE" : "DRAFT"))}
                      </Badge>
                    </td>
                    <td className="text-slate-500 font-mono text-[11px]">
                      {item.updatedAt ? format(new Date(item.updatedAt), "dd/MM/yyyy HH:mm") : "—"}
                    </td>
                    <td className="apv-col-actions text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                          title="Nhân bản bộ tiêu chuẩn QC"
                          disabled={duplicateMutation.isPending}
                          onClick={() => handleDuplicate(item)}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-rose-600 hover:text-rose-800 hover:bg-rose-50"
                          title="Xóa / Lưu trữ bộ tiêu chuẩn QC"
                          disabled={deleteMutation.isPending}
                          onClick={() => handleDelete(item)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />
    </ListPageShell>
  );
}
